import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';

import Footer from 'components/footer/footerComponent';
import InactivityModal from 'components/InactivityModal';
import Alert from 'components/alertComponent';
import { FooterTypes } from 'components/footer/footer.types';
import { ModalButtonProps } from 'components/modal/modal.interfaces';
import { NotificationsProvider } from 'notifications/contexts/NotificationsProvider';
import { ModalProvider } from 'layout/ModalProvider';
import {
  IdleTimerContext,
  useIdleTimerModalFunctionality,
} from 'hooks/useIdleTimerFunctionality';
import { useGetMessageTemplates } from 'hooks/useGetMessageTemplates';
import { messageTemplatesIds } from 'utilities/constants';
import { NOTIFICATIONS } from 'utilities/routes';
import {
  User_Notification_Status_Enum,
  useUpdateInAppNotificationMutation,
} from 'graphql/generated/hasura';
import {
  CustomNotificationActions,
  useHandleExtraActions,
} from 'hooks/useHandleExtraActions';

import { useNotifications } from '../hooks/pageLayout/useNotifications';
import { useCustomToast } from './useToast';
import { ToastNotifications } from './ToastNotifications';
import { NotificationsModal } from './NotificationsModal';
import { mapNotification } from '../utilities/functions';

export interface BaseLayoutProps {
  children?: React.ReactNode;
  footerType?: FooterTypes;
  className?: string;
  banner?: React.ReactNode;
  extraModals?: React.ReactNode;
  renderNavbar: (props: { openNavbarMobile: () => void }) => React.ReactNode;
  renderMobileNavbar: (props: {
    closeNavbarMobile: () => void;
  }) => React.ReactNode;
}

const notificationVariables = {
  isToast: true,
  includeStatuses: [
    User_Notification_Status_Enum.Delivered,
    User_Notification_Status_Enum.Seen,
  ],
};

const modalNotificationVariables = {
  includeStatuses: [
    User_Notification_Status_Enum.Delivered,
    User_Notification_Status_Enum.Seen,
  ],
  isModal: true,
  limit: 1,
  offset: 0,
};

export const BaseLayout: React.FC<BaseLayoutProps> = ({
  children,
  footerType,
  className,
  banner,
  extraModals,
  renderNavbar,
  renderMobileNavbar,
}) => {
  const navigate = useNavigate();
  const [isNavbarMobileVisible, setNavbarMobileVisible] = useState(false);
  const toggleNavMobile = () => setNavbarMobileVisible((prev) => !prev);

  const { toasts, handleAction: handleCustomToastsAction } = useCustomToast();
  const { handler: customActionsHandler } = useHandleExtraActions();
  const [updateInAppNotificationMutation] = useUpdateInAppNotificationMutation(
    {},
  );

  const { notifications: rawToasts } = useNotifications(notificationVariables);
  const { notifications: rawModals } = useNotifications(
    modalNotificationVariables,
  );

  const notifications = useMemo(
    () => rawToasts?.filter((n) => n.is_toast).map(mapNotification) ?? [],
    [rawToasts],
  );

  const modalNotification = useMemo(
    () => (rawModals?.[0] ? mapNotification(rawModals[0]) : null),
    [rawModals],
  );

  const { data: locale, loading } = useGetMessageTemplates({
    locale: 'en',
    templateId: [
      messageTemplatesIds.MY_PROFILE,
      messageTemplatesIds.MY_PERSONAL_INFO,
      messageTemplatesIds.MY_CONTACT_INFO,
      messageTemplatesIds.HEALTH_QUESTIONNAIRE,
    ],
  });

  const {
    inactivityModalOpen,
    handleStillHere,
    handleLogOut,
    componentLoading,
    componentLocale,
    remainingTime,
    pause,
    resume,
  } = useIdleTimerModalFunctionality();

  const timerContextValue = useMemo(
    () => ({ pauseTimer: pause, resumeTimer: resume }),
    [pause, resume],
  );

  const handleAction = async (
    notificationId: string,
    route: string,
    customAction?: CustomNotificationActions,
    customActionParams?: Record<string, unknown>,
    isCloseButton = false,
  ) => {
    const variables = {
      id: String(notificationId),
      status: User_Notification_Status_Enum.Read,
    };

    try {
      if (isCloseButton) {
        await updateInAppNotificationMutation({ variables });
        return;
      }

      if (customAction && customActionsHandler[customAction]) {
        await Promise.all([
          updateInAppNotificationMutation({ variables }),
          customActionsHandler[customAction](customActionParams ?? {}, false),
        ]);
      } else {
        navigate((route || '') ?? -1);
        await updateInAppNotificationMutation({ variables });
      }
    } catch (e) {
      console.error('Failed to update notification', e);
    }
  };

  const inactivityModalButtons: ModalButtonProps[] = [
    {
      label: componentLocale?.CTA2Label,
      onClick: handleStillHere,
      testID: 'still-here',
    },
    {
      label: componentLocale?.CTA1Label,
      onClick: handleLogOut,
      className: 'text-clc-blue bg-white',
      testID: 'log-out',
    },
  ];

  if ((loading && !locale) || (componentLoading && !componentLocale))
    return null;

  return (
    <ModalProvider>
      <NotificationsProvider>
        {extraModals}

        {inactivityModalOpen && (
          <InactivityModal
            inactivityModalOpen={inactivityModalOpen}
            title={componentLocale?.title}
            message={componentLocale?.message}
            timerMessage={componentLocale?.timerMessage}
            remainingTime={remainingTime}
            continueMessage={componentLocale?.continueMessage}
            buttons={inactivityModalButtons}
            onClose={handleStillHere}
          />
        )}

        {modalNotification && (
          <NotificationsModal
            notification={modalNotification}
            handleClose={() =>
              handleAction(
                modalNotification.id,
                modalNotification.action?.route ?? NOTIFICATIONS,
                modalNotification.action?.customAction,
                modalNotification.action?.customActionParams,
                true,
              )
            }
          />
        )}

        {isNavbarMobileVisible ? (
          renderMobileNavbar({ closeNavbarMobile: toggleNavMobile })
        ) : (
          <div className="relative">
            <div className="z-[1050] sticky top-0">
              {renderNavbar({ openNavbarMobile: toggleNavMobile })}
            </div>

            <div
              className={twMerge(
                'flex relative max-desktop:top-0 flex-col justify-between bg-base-content min-h-content-page-mobile desktop:min-h-content-page-web',
                className,
              )}
            >
              <div className="flex desktop:gap-30 gap-5 flex-col grow h-full pb-10 px-4 desktop:py-[60px] 2xl:px-[108px]">
                <div className="relative">
                  <div
                    data-testid="layout-api-toasts"
                    className={twMerge(
                      'absolute flex flex-col gap-2.5 desktop:-top-10 w-full',
                      [...notifications, ...toasts].length && 'z-[1000]',
                    )}
                  >
                    <ToastNotifications
                      toastNotifications={notifications}
                      handleAction={handleAction}
                    />
                    <div
                      data-testid="layout-custom-toasts"
                      className="flex flex-col gap-2.5 w-full"
                    >
                      {toasts.map((toast) => (
                        <div key={toast.id}>
                          <Alert
                            type={toast.type}
                            text={toast.message}
                            hideCloseButton={!toast.showCloseButton}
                            isNotification={toast.showCloseButton}
                            onClose={() =>
                              handleCustomToastsAction(
                                toast.id,
                                toast.action?.route,
                                toast.action?.customAction,
                                toast.action?.customActionParams,
                                true,
                              )
                            }
                            actionLabel={toast.action?.label}
                            onActionClick={() =>
                              handleCustomToastsAction(
                                toast.id,
                                toast.action?.route,
                                toast.action?.customAction,
                                toast.action?.customActionParams,
                                false,
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {banner}

                <IdleTimerContext.Provider value={timerContextValue}>
                  {children}
                </IdleTimerContext.Provider>
              </div>

              <Footer type={footerType} />
            </div>
          </div>
        )}

        <Toaster position="top-center" reverse-order={false} />
      </NotificationsProvider>
    </ModalProvider>
  );
};
